// Deep path, not the `@/shared/lib` barrel: that barrel reaches `parseSearchQuery`, which
// imports `@/shared/config`, so going through it would close a config/lib import cycle.
import type { SortOrder } from '../lib/types/sort.types';

export type SharedOrderOption = {
  label: string;
  sort: string;
  order: SortOrder;
};

const DEFAULT_SORT = {
  sort: 'createdAt',
  order: 'desc',
} as const satisfies Pick<SharedOrderOption, 'sort' | 'order'>;

export const DEFAULT_ORDER_OPTION = {
  label: 'Por arquivo mais recente',
  ...DEFAULT_SORT,
} as const satisfies SharedOrderOption;

const BASE_ORDER_OPTIONS: SharedOrderOption[] = [
  DEFAULT_ORDER_OPTION,
  { label: 'Por arquivo mais antigo', sort: 'createdAt', order: 'asc' },
  { label: 'Por ordem alfabética crescente', sort: 'name', order: 'asc' },
  { label: 'Por ordem alfabética decrescente', sort: 'name', order: 'desc' },
  { label: 'Do menor para o maior', sort: 'prefix', order: 'asc' },
  { label: 'Do maior para o menor', sort: 'prefix', order: 'desc' },
];

export const TEST_CONTENT_OPTIONS = [...BASE_ORDER_OPTIONS];

export const ORDER_OPTIONS: SharedOrderOption[] = [
  ...BASE_ORDER_OPTIONS,
  { label: 'Por número de revisões', sort: 'revision', order: 'desc' },
];

export const DOCUMENT_LIST_ORDER_OPTIONS: SharedOrderOption[] = [
  { label: 'Do menor para o maior', sort: 'prefix', order: 'asc' },
  { label: 'Do maior para o menor', sort: 'prefix', order: 'desc' },
  { label: 'Por arquivo mais recente', sort: 'effectiveDate', order: 'desc' },
  { label: 'Por arquivo mais antigo', sort: 'effectiveDate', order: 'asc' },
  { label: 'Por ordem alfabética crescente', sort: 'name', order: 'asc' },
  { label: 'Por ordem alfabética decrescente', sort: 'name', order: 'desc' },
];

export const HISTORY_DEFAULT_ORDER_OPTION = {
  label: 'Por vigência mais recente',
  sort: 'effectiveDate',
  order: 'desc',
} as const satisfies SharedOrderOption;

export const HISTORY_ORDER_OPTIONS: SharedOrderOption[] = [
  HISTORY_DEFAULT_ORDER_OPTION,
  { label: 'Por vigência mais antiga', sort: 'effectiveDate', order: 'asc' },
  ...BASE_ORDER_OPTIONS,
  { label: 'Maior número de revisões', sort: 'revision', order: 'desc' },
  { label: 'Menor número de revisões', sort: 'revision', order: 'asc' },
];

const ALPHABETICAL_ASC_LABEL = 'Ordem alfabética crescente';
const ALPHABETICAL_DESC_LABEL = 'Ordem alfabética decrescente';

const alphabeticalOrderOptions = (sort: string): SharedOrderOption[] => [
  { label: ALPHABETICAL_ASC_LABEL, sort, order: 'asc' },
  { label: ALPHABETICAL_DESC_LABEL, sort, order: 'desc' },
];

export const FUNCTIONAL_ACTIVITY_ORDER_OPTIONS = [
  { ...DEFAULT_ORDER_OPTION, label: 'Por atividade mais recente' },
  { label: 'Por atividade mais antiga', sort: 'createdAt', order: 'asc' },
  ...alphabeticalOrderOptions('name'),
];

export const PERMISSIONS_ORDER_OPTIONS: SharedOrderOption[] = [
  { ...DEFAULT_ORDER_OPTION, label: 'Cargo mais recente' },
  { label: 'Cargo mais antigo', sort: 'createdAt', order: 'asc' },
  ...alphabeticalOrderOptions('name'),
];

const matrixListOrderOptions = (dateSort: string): SharedOrderOption[] => [
  { label: 'Mais recente', sort: dateSort, order: 'desc' },
  { label: 'Mais antigo', sort: dateSort, order: 'asc' },
  ...alphabeticalOrderOptions('title'),
];

export const MATRIX_MODEL_ORDER_OPTIONS = matrixListOrderOptions('createdAt');

export const REJECTED_MATRIX_ORDER_OPTIONS = matrixListOrderOptions('rejectedAt');

export const DRAFT_MATRIX_ORDER_OPTIONS = matrixListOrderOptions('updatedAt');

export type OrderOption = (typeof ORDER_OPTIONS)[number];
export type HistoryOrderOption = (typeof HISTORY_ORDER_OPTIONS)[number];
export type TestContentOption = (typeof TEST_CONTENT_OPTIONS)[number];
